import React, { type ReactNode, useMemo, type ReactElement } from 'react';
import { ForgeUIExtensionPointProvider } from '@atlassian/forge-ui/provider';
import { ForgeUIExtensionAnalyticsContext } from '@atlassian/forge-ui/ui';
import { useEnvironment } from '@atlassian/jira-forge-ui-controllers/src/controllers/environment/index.tsx';
import type { Extension } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import { useHasForgeAccess } from '@atlassian/jira-forge-ui-utils-internal/src/utils/forge-access/index.tsx';
import { getAnalyticsWebClientPromise } from '@atlassian/jira-product-analytics-web-client-async';

type Props = {
	extension: Extension;
	localId: string;
	children: ReactNode;
	renderNoAccessFallback?: () => ReactElement;
};

export const ExtensionProvider = ({
	children,
	extension,
	localId,
	renderNoAccessFallback,
}: Props): React.JSX.Element | null => {
	const memoRenderNoAccessFallback = useMemo(
		() => renderNoAccessFallback || (() => null),
		[renderNoAccessFallback],
	);

	const hasForgeAccess = useHasForgeAccess();
	const environment = useEnvironment();

	/**
	 * Since we do not have docs for the metal client change, I'll link notes from the internal discussion:
	 * https://atlassian.slack.com/archives/CK2KJ2EVD/p1589267473262900?thread_ts=1589205176.225800&cid=CK2KJ2EVD
	 *
	 * `environment` is the product environment
	 * `product` is `jira` or `confluence`
	 * `page` is essentially just a tag that we add onto each of the events
	 *    to distinguish between events, e.g. for Confluence’s Editor it is `editPageScreen`
	 *    (i.e. like source in an analytics event)
	 */

	const metalClientProps = {
		page: extension.type,
		environment,
		product: 'jira',
	};

	return hasForgeAccess ? (
		<ForgeUIExtensionPointProvider
			{...metalClientProps}
			// @ts-expect-error - TS2322 - Type 'Promise<typeof AnalyticsWebClient | AnalyticsWebClient>' is not assignable to type 'AnalyticsWebClient | Promise<AnalyticsWebClient>'.
			analyticsWebClient={getAnalyticsWebClientPromise().then((client) => client.getInstance())}
		>
			<ForgeUIExtensionAnalyticsContext localId={localId} extensionId={extension.id}>
				{children}
			</ForgeUIExtensionAnalyticsContext>
		</ForgeUIExtensionPointProvider>
	) : (
		memoRenderNoAccessFallback()
	);
};
